import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, X } from "lucide-react";

/**
 * DemoAutoTour — cinematic guided walkthrough of the FORGEDOPS demo.
 *
 * Mission-critical operations briefing — NOT a marketing slideshow.
 *
 * Behaviour:
 *  - Triggered by `tour.start()` (exposed via the hook below) OR by
 *    pressing ⇧Ω (Shift+T) anywhere on /demo.
 *  - Scrolls through scripted sections of the page in sequence with
 *    smooth-scroll, per-step caption overlay (chapter / headline /
 *    sub-line), and an unobtrusive scrubber at the bottom.
 *  - Pause / resume mid-tour. ESC or × ends the tour and drops the
 *    viewer back at the role-tiles section ("ENTER THE SYSTEM").
 *  - Soft cinematic vignette + scanline overlay during the tour.
 *
 * Tone: industrial, intelligent, operational. No flash. No bounce.
 */

const STEPS = [
  {
    sel: "section header, header",
    chapter: "00 · OPERATIONAL IDENTITY",
    headline: "FORGEDOPS",
    sub: "Heavy civil operations command. Not a dashboard. A working system.",
    hold: 4200,
  },
  {
    sel: "#roles",
    chapter: "01 · ENTER THE SYSTEM",
    headline: "Seven roles. One-click access.",
    sub: "Every prospect signs in as anyone. No password walls. The system reacts to who you are.",
    hold: 5200,
  },
  {
    sel: "[data-testid='ops-activity-feed-list'], section:has([data-testid='demo-activity-card'])",
    chapter: "02 · LIVE OPERATIONS FEED",
    headline: "Every action — visible in real time.",
    sub: "Daily reports · pre-ops · incidents · meetings · QA/QC. Same heartbeat for crews and PMs.",
    hold: 5400,
  },
  {
    sel: "#workflows",
    chapter: "03 · CAUSE → EFFECT → CONSEQUENCE",
    headline: "Submit something. Watch the system act.",
    sub: "Pre-Op FAIL locks out equipment. Incidents alert Safety. Daily reports route to PM. No manual email.",
    hold: 6000,
  },
  {
    sel: "[data-testid='adoption-bilingual'], [data-testid='field-first-workflows']",
    chapter: "04 · ADOPTION INFRASTRUCTURE",
    headline: "Built so the field actually uses it.",
    sub: "Sub-minute submissions. QR sign-offs. Photo-enforced FAIL paths. Bilingual EN / ES. The system the crews don't fight.",
    hold: 5200,
  },
  {
    sel: "[data-testid='adoption-whitelabel']",
    chapter: "04 · YOUR COMPANY · YOUR SYSTEM",
    headline: "Brand-fit deployments.",
    sub: "Logos, colors, terminology, forms, PDFs, notifications — your operation, your interface.",
    hold: 4800,
  },
  {
    sel: "[data-testid='mobile-showcase']",
    chapter: "06 · BUILT FOR THE TRUCK CAB",
    headline: "Field-first by design.",
    sub: "Photo enforcement on FAIL. GPS + weather auto-load. Offline-friendly. One-handed flows.",
    hold: 5800,
  },
  {
    sel: "[data-testid='training-hub-showcase']",
    chapter: "07 · TRAINING WORKFORCE ADOPTION",
    headline: "OSHA outreach · equipment · safety · leadership.",
    sub: "Bilingual lessons. Per-crew progress. QR onboarding. Completions land in the operator's record.",
    hold: 5600,
  },
  {
    sel: "[data-testid='demo-role-super'], #roles",
    chapter: "ENTER",
    headline: "This isn't software. It's operations.",
    sub: "Built by people who run the work — for the people who run the work. Pick the Superintendent and control the operation. Every action persists. Every submission routes.",
    hold: 5800,
    isFinal: true,
    spotlight: "[data-testid='demo-role-super']",
  },
];

let _instance = null;
export function startAutoTour()  { if (_instance) _instance.start(); }
export function stopAutoTour()   { if (_instance) _instance.stop(); }

export default function DemoAutoTour() {
  const [active, setActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const stepIdxRef = useRef(0);
  const pausedRef  = useRef(false);
  const timerRef   = useRef(null);

  useEffect(() => { stepIdxRef.current = stepIdx; }, [stepIdx]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // Public API
  useEffect(() => {
    _instance = {
      start: () => { setStepIdx(0); setPaused(false); setActive(true); },
      stop:  () => { setActive(false); setPaused(false); clearTimeout(timerRef.current); },
    };
    return () => { _instance = null; };
  }, []);

  // Hotkey: Shift+T starts the tour. ESC ends it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.shiftKey && (e.key === "T" || e.key === "t")) {
        e.preventDefault();
        if (active) { setActive(false); }
        else        { setStepIdx(0); setPaused(false); setActive(true); }
      }
      if (e.key === "Escape" && active) {
        setActive(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  // Step driver
  useEffect(() => {
    if (!active) { clearTimeout(timerRef.current); return; }
    const step = STEPS[stepIdx];
    if (!step) { setActive(false); return; }

    // Scroll target into view
    const el = document.querySelector(step.sel);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Schedule next step
    const tick = () => {
      if (pausedRef.current) {
        timerRef.current = setTimeout(tick, 200);
        return;
      }
      const next = stepIdxRef.current + 1;
      if (next >= STEPS.length) {
        setActive(false);
        // drop viewer at #roles for the click-through
        const r = document.querySelector("#roles");
        if (r) r.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      setStepIdx(next);
    };
    timerRef.current = setTimeout(tick, step.hold);
    return () => clearTimeout(timerRef.current);
  }, [active, stepIdx]);

  if (!active) return null;
  const step = STEPS[stepIdx];
  if (!step) return null;
  const total = STEPS.length;
  const progressPct = ((stepIdx + 1) / total) * 100;

  return (
    <>
      {/* Cinematic vignette + scanline (intentionally subtle) */}
      <div
        aria-hidden
        className="fixed inset-0 z-[9990] pointer-events-none animate-fadein"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 z-[9991] pointer-events-none mix-blend-overlay"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 3px)",
          opacity: 0.5,
        }}
      />

      {/* Outro spotlight — bright halo around target tile on the final step */}
      {step.spotlight && <Spotlight selector={step.spotlight} />}
      {/* Top caption strip */}
      <div
        data-testid="autotour-caption"
        className="fixed top-0 inset-x-0 z-[9995] pointer-events-none"
      >
        <div className="caution-stripe" />
        <div
          className="px-5 sm:px-10 py-5 sm:py-6 backdrop-blur-md border-b animate-fadein"
          style={{
            background: "rgba(8,8,8,0.78)",
            borderColor: "rgba(255,106,0,0.25)",
          }}
          key={stepIdx}
        >
          <div className="max-w-7xl mx-auto flex items-start gap-4 sm:gap-6">
            <span
              className="hidden sm:inline-flex items-center justify-center w-12 h-12 rounded-md shrink-0 mt-1"
              style={{
                background: "rgba(255,106,0,0.15)",
                color: "#FF6A00",
                border: "1px solid rgba(255,106,0,0.45)",
              }}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] font-bold">
                {String(stepIdx + 1).padStart(2, "0")}
              </span>
            </span>
            <div className="flex-1 min-w-0">
              <div
                className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.4em] mb-1"
                style={{ color: "#FF6A00" }}
              >
                {step.chapter}
              </div>
              <div className="font-display text-xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                {step.headline}
              </div>
              <div
                className="text-xs sm:text-sm mt-1.5 max-w-3xl leading-relaxed"
                style={{ color: "#BDBDBD" }}
              >
                {step.sub}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em]" style={{ color: "#FF6A00" }}>
                Auto Tour
              </div>
              <div className="font-mono text-[10px]" style={{ color: "#BDBDBD" }}>
                {stepIdx + 1} / {total}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scrubber + controls */}
      <div
        className="fixed bottom-0 inset-x-0 z-[9995] backdrop-blur-md border-t"
        style={{
          background: "rgba(8,8,8,0.85)",
          borderColor: "rgba(255,106,0,0.25)",
        }}
      >
        <div
          className="h-0.5 transition-all duration-300"
          style={{ width: `${progressPct}%`, background: "#FF6A00" }}
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-3">
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="autotour-pause"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded transition"
            style={{
              background: paused ? "rgba(255,106,0,0.18)" : "transparent",
              color: paused ? "#FF6A00" : "#BDBDBD",
              border: `1px solid ${paused ? "rgba(255,106,0,0.45)" : "#2A2A2A"}`,
            }}
          >
            {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
            {paused ? "Resume" : "Pause"}
          </button>
          <div className="flex-1 flex items-center gap-1 overflow-x-auto">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStepIdx(i)}
                aria-label={`Jump to step ${i + 1}`}
                className="h-1 rounded-full shrink-0 transition-all"
                style={{
                  width: i === stepIdx ? 28 : 14,
                  background: i <= stepIdx ? "#FF6A00" : "#1F1F1F",
                  opacity: i === stepIdx ? 1 : i < stepIdx ? 0.7 : 0.4,
                }}
              />
            ))}
          </div>
          <span className="hidden sm:inline-block font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#8A8A8A" }}>
            ⇧T to toggle · ESC to exit
          </span>
          <button
            onClick={() => { setActive(false); }}
            data-testid="autotour-exit"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded transition"
            style={{
              background: "transparent",
              color: "#BDBDBD",
              border: "1px solid #2A2A2A",
            }}
          >
            <X className="w-3 h-3" /> Exit
          </button>
        </div>
      </div>
    </>
  );
}

/** Compact CTA pill — "▷ Watch 60s tour" — drop into the hero. */
export function AutoTourLaunchButton({ className = "", testId = "autotour-launch" }) {
  return (
    <button
      type="button"
      onClick={() => startAutoTour()}
      data-testid={testId}
      className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 rounded transition group ${className}`}
      style={{
        background: "rgba(255,106,0,0.10)",
        color: "#FF6A00",
        border: "1px solid rgba(255,106,0,0.45)",
      }}
      title="Press Shift+T to start the cinematic tour"
    >
      <span
        className="inline-flex items-center justify-center w-5 h-5 rounded-full transition-transform group-hover:scale-110"
        style={{ background: "#FF6A00", color: "#000" }}
      >
        <Play className="w-2.5 h-2.5 ml-0.5" fill="currentColor" />
      </span>
      Watch 60s operational briefing
      <span className="hidden sm:inline font-mono text-[9px] tracking-[0.15em] px-1.5 py-0.5 rounded" style={{ background: "#000", color: "#FF6A00" }}>
        ⇧T
      </span>
    </button>
  );
}

/**
 * Spotlight — pulsing operational halo around a target element. Used by
 * the final tour step to direct the viewer's eye toward the Demo
 * Superintendent role tile, turning the tour outro into a sharp CTA
 * without ANY motion gimmickry.
 */
function Spotlight({ selector }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let raf;
    const update = () => {
      const el = document.querySelector(selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    raf = setInterval(update, 220);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      clearInterval(raf);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [selector]);

  if (!rect) return null;
  const pad = 14;

  return (
    <>
      {/* Halo */}
      <div
        aria-hidden
        className="fixed pointer-events-none z-[9992] rounded-xl animate-fadein"
        style={{
          top:    rect.top - pad,
          left:   rect.left - pad,
          width:  rect.width + pad * 2,
          height: rect.height + pad * 2,
          boxShadow:
            "0 0 0 1px rgba(255,106,0,0.85), 0 0 60px 8px rgba(255,106,0,0.55), 0 0 120px 18px rgba(255,106,0,0.35)",
          border: "1px solid rgba(255,106,0,0.85)",
          background: "rgba(255,106,0,0.04)",
        }}
      />
      {/* Corner brackets — tight industrial feel */}
      {["tl", "tr", "bl", "br"].map((corner) => (
        <Bracket key={corner} corner={corner} rect={rect} pad={pad} />
      ))}
      {/* Outro CTA chip — sits just below the spotlighted tile */}
      <div
        className="fixed z-[9993] pointer-events-none animate-fadein"
        style={{
          top: rect.top + rect.height + pad + 12,
          left: rect.left,
          width: rect.width,
        }}
      >
        <div
          className="inline-flex flex-col items-start gap-1.5 px-3.5 py-2.5 rounded"
          style={{
            background: "#0A0A0A",
            border: "1px solid rgba(255,106,0,0.6)",
            color: "#FF6A00",
          }}
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.3em]">Recommended entry</span>
          <span className="font-display text-sm font-black tracking-tight" style={{ color: "#FFFFFF" }}>
            Click Superintendent · run the work
          </span>
          <span className="font-mono text-[8px] uppercase tracking-[0.25em] mt-1" style={{ color: "#BDBDBD" }}>
            Built for the field. Ready to run.
          </span>
        </div>
      </div>
    </>
  );
}

function Bracket({ corner, rect, pad }) {
  const size = 18;
  const T = corner.startsWith("t");
  const L = corner.endsWith("l");
  const top    = T ? rect.top - pad - 1 : rect.top + rect.height + pad - size + 1;
  const left   = L ? rect.left - pad - 1 : rect.left + rect.width + pad - size + 1;
  const borderTop    = T ? "2px solid #FF6A00" : "none";
  const borderBottom = T ? "none" : "2px solid #FF6A00";
  const borderLeft   = L ? "2px solid #FF6A00" : "none";
  const borderRight  = L ? "none" : "2px solid #FF6A00";
  return (
    <div
      aria-hidden
      className="fixed z-[9993] pointer-events-none"
      style={{ top, left, width: size, height: size, borderTop, borderBottom, borderLeft, borderRight }}
    />
  );
}
